export const convertUnixTimeToDate = (unixTime: number) => {
  // Check if the date is already in milliseconds
  const charCount = (new Date()).getTime().toString().length;
  if (unixTime.toString().length < charCount){
    // Date(unixTime) will expect milliseconds
    unixTime = unixTime * 1000
  }
  // Convert to human readable text
  const date = new Date(unixTime);
  return convertDate(date);
};

export const convertDatetoReadableFormat = (unixTime) => {
  // Check if the date is already in milliseconds
  const charCount = (new Date()).getTime().toString().length;
  if (unixTime.toString().length < charCount){
    // Date(unixTime) will expect milliseconds
    unixTime = unixTime * 1000
  }
  // Convert to human readable text
  return convertDate(new Date(unixTime));
};

export const convertDate = (date: Date) => {
  return `${date.getUTCDate()} ${date.toLocaleString("default", {
    month: "short",
    timeZone: "UTC",
  })} ${date.getUTCFullYear()} ${date.toLocaleTimeString("en-US", {
    timeZone: "UTC",
  })}`;
};


/**
 * Formats a timestamp into a human-readable format for message timestamps
 * @param timestamp ISO timestamp string
 * @returns Human-readable time representation (e.g. "Just now", "2 min ago", "Today at 3:24 PM")
 */
export const formatMessageTime = (timestamp: string): string => {
  if (!timestamp) return "";

  const messageDate = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor(
    (now.getTime() - messageDate.getTime()) / 1000
  );

  // Just now (less than 1 minute ago)
  if (diffInSeconds < 60) {
    return "Just now";
  }

  // Minutes ago (less than 1 hour)
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
  }

  // Hours ago (less than 24 hours, but still today)
  if (
    diffInSeconds < 86400 &&
    messageDate.getDate() === now.getDate() &&
    messageDate.getMonth() === now.getMonth() &&
    messageDate.getFullYear() === now.getFullYear()
  ) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }

  // Format time part (for "Today at", "Yesterday at", etc.)
  const formatTimeString = (date: Date): string => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert 24h to 12h format
    hours = hours % 12;
    hours = hours ? hours : 12; // Handle midnight (0 hours)

    // Add leading zero to minutes if needed
    const minutesStr = minutes < 10 ? "0" + minutes : minutes;

    return `${hours}:${minutesStr} ${ampm}`;
  };

  // Today
  if (
    messageDate.getDate() === now.getDate() &&
    messageDate.getMonth() === now.getMonth() &&
    messageDate.getFullYear() === now.getFullYear()
  ) {
    return `Today at ${formatTimeString(messageDate)}`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear()
  ) {
    return `Yesterday at ${formatTimeString(messageDate)}`;
  }

  // Within last 7 days
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  if (messageDate >= oneWeekAgo) {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return `${days[messageDate.getDay()]} at ${formatTimeString(messageDate)}`;
  }

  // This year
  if (messageDate.getFullYear() === now.getFullYear()) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${
      months[messageDate.getMonth()]
    } ${messageDate.getDate()} at ${formatTimeString(messageDate)}`;
  }

  // Different year
  return `${messageDate.getFullYear()}-${(messageDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${messageDate
    .getDate()
    .toString()
    .padStart(2, "0")} at ${formatTimeString(messageDate)}`;
};